import { useEffect, useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField,
} from '@mui/material';

const isPositiveInteger = (value: string) => /^[1-9]\d*$/.test(value.trim());

export default function RefundMaxAmountDialog({
  open, description, initialCents, onClose, onSave, onReset, resetLabel = 'Use company default',
}: {
  open: boolean;
  description: React.ReactNode;
  initialCents: number | null | undefined;
  onClose: () => void;
  onSave: (cents: number) => Promise<void>;
  onReset?: () => Promise<void>;
  resetLabel?: string;
}) {
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setInput(initialCents != null ? String(Math.round(initialCents / 100)) : '');
      setError(null);
    }
  }, [open, initialCents]);

  const invalid = input.trim() !== '' && !isPositiveInteger(input);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Max Refund Amount</DialogTitle>
      <DialogContent>
        <DialogContentText component="div" sx={{ mb: 2 }}>{description}</DialogContentText>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField
          fullWidth
          type="number"
          label="Max refund amount (USD)"
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setError(null);
          }}
          error={invalid}
          helperText={invalid ? 'Enter a whole dollar amount greater than 0.' : undefined}
          inputProps={{ min: 1, step: 1 }}
        />
      </DialogContent>
      <DialogActions>
        {onReset && (
          <Button
            sx={{ mr: 'auto' }}
            onClick={async () => {
              try {
                await onReset();
                onClose();
              } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to save');
              }
            }}
          >
            {resetLabel}
          </Button>
        )}
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={invalid || input.trim() === ''}
          onClick={async () => {
            try {
              await onSave(Number(input.trim()) * 100);
              onClose();
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Failed to save');
            }
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
